import { gql } from 'graphql-request'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import ReactPlayer from 'react-player'

import { useMutation } from '@hooks/graphql'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@components/ui/form'
import SaveButton from './SaveButton'
import Notification from '@components/Notification'

const UPDATE_VIDEO_BLOCK_MUTATION = gql`
  mutation updateVideoBlock($input: UpdateVideoBlockInput!) {
    updateVideoBlock(input: $input) {
      video {
        url
      }
      errors {
        message
      }
    }
  }
`

const formatUrl = (url) => {
  if (!url) return null

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }

  return `https://${url}`
}

const VideoForm = ({ id, url, refetch }) => {
  const [videoErrored, setVideoErrored] = useState(false)
  const [saving, setSaving] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(() => formatUrl(url))

  const form = useForm({
    mode: 'onChange',
    defaultValues: { url }
  })

  const watchedValues = form.watch()

  const { mutate: updateVideoBlock, isLoading } = useMutation({
    gqlMutation: UPDATE_VIDEO_BLOCK_MUTATION,
    onSuccess: () => {
      refetch()
      setSaving(false)
      form.reset(watchedValues)
    }
  })

  const handleSave = () => {
    setSaving(true)
    const formattedUrl = formatUrl(watchedValues.url)
    updateVideoBlock({ input: { id, url: formattedUrl } })
  }

  const handleUrlBlur = () => {
    setVideoErrored(false)
    const currentUrl = form.getValues('url')
    setPreviewUrl(formatUrl(currentUrl))
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(() => {})} className='flex flex-col'>
        <FormField
          control={form.control}
          name='url'
          rules={{ required: 'Video URL is required' }}
          render={({ field }) => (
            <FormItem className='mb-1'>
              <FormLabel htmlFor='video-url'>Video URL</FormLabel>
              <FormControl>
                <Input
                  id='video-url'
                  placeholder='https://www.youtube.com/watch?v=dQw4w9WgXcQ'
                  {...field}
                  onBlur={(e) => {
                    field.onBlur(e)
                    handleUrlBlur()
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <small className='mt-2 mb-4'>You can add videos from Youtube, Vimeo, Facebook and Twitch.</small>

        <Choose>
          <When condition={videoErrored}>
            <Notification className='w-fit mb-4' theme='error'>
              Oops! Something went wrong while loading your video. Please check if the URL is valid.
            </Notification>
          </When>
          <When condition={previewUrl}>
            <div className='mb-4 flex justify-center'>
              <div className='w-full max-w-3xl aspect-video'>
                <ReactPlayer
                  width='100%'
                  height='100%'
                  controls
                  url={previewUrl}
                  onError={() => setVideoErrored(true)}
                  className='bg-gray-200'
                  key={previewUrl}
                />
              </div>
            </div>
          </When>
        </Choose>

        <div className='flex justify-end mt-3'>
          <SaveButton
            saving={saving}
            hasChanges={form.formState.isDirty}
            isLoading={isLoading}
            onSave={handleSave}
          />
        </div>
      </form>
    </Form>
  )
}

export default VideoForm
